/**
 * Auth context: login/signup via backend API, session in localStorage.
 * Use getAuthHeader() when calling protected APIs.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  phone?: string;
}

interface Session {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signup: (
    email: string,
    phone: string,
    password: string,
    confirmPassword: string
  ) => Promise<{ success: boolean; error?: string }>;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  getAuthHeader: () => { Authorization: string } | Record<string, never>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = (import.meta.env.VITE_API_URL || `${window.location.origin}/api`).replace(
  /\/$/,
  ''
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSession = async () => {
      try {
        const storedSession = localStorage.getItem('auth_session');
        const storedUser = localStorage.getItem('auth_user');

        if (storedSession && storedUser) {
          const parsedSession = JSON.parse(storedSession) as Session;
          const parsedUser = JSON.parse(storedUser) as User;
          const now = Math.floor(Date.now() / 1000);
          if (parsedSession.expires_at && parsedSession.expires_at > now) {
            setSession(parsedSession);
            setUser(parsedUser);
          } else {
            localStorage.removeItem('auth_session');
            localStorage.removeItem('auth_user');
          }
        }
      } catch (error) {
        console.error('Error loading session:', error);
        localStorage.removeItem('auth_session');
        localStorage.removeItem('auth_user');
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, []);

  const signup = async (
    email: string,
    phone: string,
    password: string,
    confirmPassword: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone, password, confirmPassword })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Signup failed' };
      }

      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 503 && data.error?.includes('not configured')) {
          // Backend has no Supabase; allow dev access without real login
          const devUser = { id: 'dev-user', email: email || 'dev@example.com' };
          const devSession = {
            access_token: 'dev-token',
            refresh_token: '',
            expires_at: Math.floor(Date.now() / 1000) + 86400
          };
          setUser(devUser);
          setSession(devSession);
          localStorage.setItem('auth_session', JSON.stringify(devSession));
          localStorage.setItem('auth_user', JSON.stringify(devUser));
          return { success: true };
        }
        return { success: false, error: data.error || 'Login failed' };
      }

      if (!data.success) {
        return { success: false, error: data.error || 'Login failed' };
      }

      setUser(data.user);
      setSession(data.session);
      localStorage.setItem('auth_session', JSON.stringify(data.session));
      localStorage.setItem('auth_user', JSON.stringify(data.user));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const forgotPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to send reset email' };
      }

      return { success: true };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (session?.access_token) {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setSession(null);
      localStorage.removeItem('auth_session');
      localStorage.removeItem('auth_user');
      // Fresh start next sign-in: clear Data Validator cached report/chat
      localStorage.removeItem('inventory-reconciliation-report');
    }
  };

  const getAuthHeader = (): { Authorization: string } | Record<string, never> => {
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
    return {};
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signup,
        login,
        forgotPassword,
        logout,
        getAuthHeader
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
